import { Vec3 as PcVec3 } from 'playcanvas';
import {
  Vec3,
  type CameraOptions,
  type ProjectedPoint,
  type ScreenshotOptions,
  type Vec3Like,
  type ViewerCameraApi,
} from '@mindcloudxai/shared';
import type { ViewerContext } from '../types';
import { tmpWorld, tmpView, tmpScreen } from '../constants';

const Y_UP = new PcVec3(0, 1, 0);

export function createCameraApi(
  getCtx: () => ViewerContext,
  assertInitialized: () => void,
): ViewerCameraApi {
  return {
    get: () => {
      assertInitialized();
      return getCamera(getCtx());
    },
    set: (camera) => {
      assertInitialized();
      setCamera(getCtx(), camera);
    },
    pick: (options) => {
      assertInitialized();
      return pick(getCtx(), options);
    },
    project: (point) => {
      assertInitialized();
      return project(point, getCtx());
    },
    captureScreenshot: (options) => {
      assertInitialized();
      return captureScreenshot(getCtx(), options);
    },
  };
}

export function getCamera(ctx: ViewerContext): CameraOptions {
  const { camera, viewer } = ctx;
  const pos = camera.getPosition();
  const target = new PcVec3();
  if (viewer?.cameraManager) {
    viewer.cameraManager.camera.calcFocusPoint(target);
  } else {
    target.copy(pos).add(camera.forward);
  }
  // 从 PlayCanvas Entity 的 up 向量获取相机 roll 信息
  const entityUp = camera.up;
  // 当 up 与 Y-up 差异显著时才输出，避免无 roll 时增加冗余数据
  const upVec =
    entityUp.dot(Y_UP) < 0.999 ? { x: entityUp.x, y: entityUp.y, z: entityUp.z } : undefined;
  return {
    position: { x: pos.x, y: pos.y, z: pos.z },
    target: { x: target.x, y: target.y, z: target.z },
    fov: camera.camera?.fov,
    near: camera.camera?.nearClip,
    far: camera.camera?.farClip,
    up: upVec,
  };
}

function setCamera(ctx: ViewerContext, cam: CameraOptions): void {
  const { camera, viewer, app } = ctx;
  camera.setPosition(cam.position.x, cam.position.y, cam.position.z);
  camera.lookAt(new PcVec3(cam.target.x, cam.target.y, cam.target.z));
  if (cam.fov !== undefined && camera.camera) camera.camera.fov = cam.fov;
  if (cam.near !== undefined && camera.camera) camera.camera.nearClip = cam.near;
  if (cam.far !== undefined && camera.camera) camera.camera.farClip = cam.far;
  if (viewer?.cameraManager) {
    const managedCamera = viewer.cameraManager.camera;
    const upPcVec = cam.up ? new PcVec3(cam.up.x, cam.up.y, cam.up.z) : undefined;
    managedCamera.look(
      new PcVec3(cam.position.x, cam.position.y, cam.position.z),
      new PcVec3(cam.target.x, cam.target.y, cam.target.z),
      upPcVec,
    );
    if (cam.fov !== undefined) {
      managedCamera.fov = cam.fov;
    }
    viewer.cameraManager.snap();
  }
  app.renderNextFrame = true;
}

async function pick(
  ctx: ViewerContext,
  options: { clientX: number; clientY: number },
): Promise<{ point: Vec3 } | null> {
  const picker = ctx.viewer?.picker;
  if (!picker || ctx.disposed) return null;
  const rect = ctx.canvas.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return null;
  const u = (options.clientX - rect.left) / rect.width;
  const v = (options.clientY - rect.top) / rect.height;
  if (u < 0 || u > 1 || v < 0 || v > 1) return null;
  const point = await picker.pick(u, v);
  return point ? { point: new Vec3(point.x, point.y, point.z) } : null;
}

function project(point: Vec3Like, ctx: ViewerContext): ProjectedPoint | null {
  if (ctx.disposed) return null;
  const camComp = ctx.camera.camera;
  if (!camComp) return null;

  tmpWorld.set(point.x, point.y, point.z);
  // 视空间深度判定前后（相机朝向 -z）
  camComp.viewMatrix.transformPoint(tmpWorld, tmpView);
  const behind = tmpView.z >= 0;

  camComp.worldToScreen(tmpWorld, tmpScreen);
  const rect = ctx.canvas.getBoundingClientRect();
  const clientX = rect.left + tmpScreen.x;
  const clientY = rect.top + tmpScreen.y;

  return {
    clientX,
    clientY,
    depth: -tmpView.z,
    visible:
      !behind &&
      tmpScreen.x >= 0 &&
      tmpScreen.x <= rect.width &&
      tmpScreen.y >= 0 &&
      tmpScreen.y <= rect.height,
  };
}

async function captureScreenshot(
  ctx: ViewerContext,
  options: ScreenshotOptions = {},
): Promise<Blob> {
  const { app, canvas } = ctx;
  app.renderNextFrame = true;
  app.render();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('captureScreenshot: toBlob returned null'));
      },
      options.mimeType ?? 'image/png',
      options.quality,
    );
  });
}
